import { GamePlayScene } from '../scenes/GamePlayScene';
import { getEquipmentManager } from '../entities/base/UserProfile';
import { AudioManager, SoundEffect } from '../systems/AudioManager';
import { t } from '../i18n/lang';

export interface RewardCalculation {
  baseXP: number;
  bonusXP: number;
  totalXP: number;
  baseGold: number;
  bonusGold: number;
  totalGold: number;
  baseRuby: number;
  bonusRuby: number;
  totalRuby: number;
}

export interface LevelUpResult {
  leveledUp: boolean;
  newLevel: number;
  unlockedContent: string[];
}

export class RewardManager {
  private scene: GamePlayScene;
  private audioManager: AudioManager;
  // Progress tracking cho ván chơi
  private gameXP: number = 0;
  private gameLevel: number = 1;
  private totalMonsterKills: number = 0;
  private totalBossKills: number = 0;
  private sessionTime: number = 0;

  constructor(scene: GamePlayScene) {
    this.scene = scene;
    this.audioManager = AudioManager.getInstance(scene);
    this.sessionTime = 0;
  }

  // Luôn khởi tạo ván mới với level 1, XP 0
  initializeNewGame(): void {
    this.gameLevel = 1;
    this.gameXP = 0;
    this.totalMonsterKills = 0;
    this.totalBossKills = 0;
    this.sessionTime = 0;
  }

  // Không lấy từ profile nữa
  initializeFromProfile(): void {
    this.initializeNewGame();
  }

  addXP(amount: number): LevelUpResult {
    const equipmentStats = getEquipmentManager().calculateTotalStats();
    const xpMultiplier = 1 + (equipmentStats.xpBonus || 0);
    const bonusXP = Math.floor(amount * (xpMultiplier - 1));
    const totalXP = amount + bonusXP;
    this.gameXP += totalXP;
    // Check for level up
    let leveledUp = false;
    let unlockedContent: string[] = [];
    const xpRequired = this.getXPRequiredForLevel(this.gameLevel + 1);
    if (this.gameXP >= xpRequired) {
      leveledUp = true;
      this.gameLevel++;
      this.gameXP -= xpRequired;
      this.audioManager.playSFX(SoundEffect.LEVEL_UP);
      unlockedContent = this.checkUnlockedContent(this.gameLevel);
      this.showLevelUpEffect();
    }
    return {
      leveledUp,
      newLevel: this.gameLevel,
      unlockedContent
    };
  }

  applyBalancedRewards(monsterKills: number, bossKills: number, currentLevel: number, timePlayed: number): RewardCalculation {
    // Calculate base rewards
    const baseXP = this.calculateBaseXP(monsterKills, bossKills, currentLevel, timePlayed);
    const baseGold = this.calculateBaseGold(monsterKills, bossKills, currentLevel);
    const baseRuby = this.calculateBaseRuby(bossKills, currentLevel);
    
    // Apply equipment bonuses
    const equipmentStats = getEquipmentManager().calculateTotalStats();
    const xpMultiplier = 1 + (equipmentStats.xpBonus || 0);
    const goldMultiplier = 1 + (equipmentStats.goldBonus || 0);
    const rubyMultiplier = 1 + (equipmentStats.rubyBonus || 0);
    
    const bonusXP = Math.floor(baseXP * (xpMultiplier - 1));
    const bonusGold = Math.floor(baseGold * (goldMultiplier - 1));
    const bonusRuby = Math.floor(baseRuby * (rubyMultiplier - 1));
    
    return {
      baseXP,
      bonusXP,
      totalXP: baseXP + bonusXP,
      baseGold,
      bonusGold,
      totalGold: baseGold + bonusGold,
      baseRuby,
      bonusRuby,
      totalRuby: baseRuby + bonusRuby
    };
  }

  onMonsterKilled(monsterType: string = 'basic'): number {
    this.totalMonsterKills++;
    
    // XP rewards based on monster type
    let xpReward = 10;
    switch (monsterType) {
      case 'fast': xpReward = 15; break;
      case 'tank': xpReward = 25; break;
      case 'shielded': xpReward = 20; break;
      case 'splitting': xpReward = 30; break;
      default: xpReward = 10; break;
    }
    
    const levelResult = this.addXP(xpReward);
    if (levelResult.leveledUp) {
      this.showUnlockedContent(levelResult.unlockedContent);
    }
    
    return xpReward;
  }

  onBossKilled(bossType: string = 'basic'): number {
    this.totalBossKills++;
    
    // Boss XP rewards
    let xpReward = 100;
    switch (bossType) {
      case 'shield': xpReward = 150; break;
      case 'summoner': xpReward = 200; break;
      case 'ultimate': xpReward = 500; break;
      default: xpReward = 100; break;
    }
    
    const levelResult = this.addXP(xpReward);
    if (levelResult.leveledUp) {
      this.showUnlockedContent(levelResult.unlockedContent);
    }
    
    // Play boss kill sound
    this.audioManager.playSFX(SoundEffect.BOSS_DEATH);
    
    return xpReward;
  }

  levelUp(): boolean {
    const xpRequired = this.getXPRequiredForLevel(this.gameLevel + 1);
    if (this.gameXP >= xpRequired) {
      this.gameLevel++;
      this.gameXP -= xpRequired;
      
      // Play level up sound
      this.audioManager.playSFX(SoundEffect.LEVEL_UP);
      
      // Check for unlocked content
      const unlockedContent = this.checkUnlockedContent(this.gameLevel);
      this.showLevelUpEffect();
      this.showUnlockedContent(unlockedContent);
      
      return true;
    }
    return false;
  }

  unlockNewContent(level: number): string[] {
    const unlocks: string[] = [];
    
    // Earth unlocks
    if (level === 5) unlocks.push('IceEarth');
    if (level === 10) unlocks.push('VolcanicEarth');
    if (level === 15) unlocks.push('CrystalEarth');
    if (level === 20) unlocks.push('QuantumEarth');
    if (level === 25) unlocks.push('NeutronEarth');
    
    // Tower unlocks
    if (level === 3) unlocks.push('RapidFireTower');
    if (level === 6) unlocks.push('HeavyCannon');
    if (level === 8) unlocks.push('MissileTower');
    if (level === 12) unlocks.push('FreezeTower');
    if (level === 16) unlocks.push('PiercingTower');
    if (level === 20) unlocks.push('ChainLightningTower');
    
    // Pet unlocks
    if (level === 4) unlocks.push('LaserPet');
    if (level === 9) unlocks.push('MissilePet');
    if (level === 14) unlocks.push('DronePet');
    if (level === 19) unlocks.push('ShieldPet');
    if (level === 24) unlocks.push('SupportPet');
    
    return unlocks;
  }

  private calculateBaseXP(monsterKills: number, bossKills: number, currentLevel: number, timePlayed: number): number {
    const monsterXP = monsterKills * 10;
    const bossXP = bossKills * 100;
    const levelBonus = currentLevel * 5;
    const timeBonus = Math.floor(timePlayed / 60000) * 20; // 20 XP per minute
    
    return monsterXP + bossXP + levelBonus + timeBonus;
  }

  private calculateBaseGold(monsterKills: number, bossKills: number, currentLevel: number): number {
    const monsterGold = monsterKills * 5;
    const bossGold = bossKills * 50;
    const levelBonus = currentLevel * 10;
    
    return monsterGold + bossGold + levelBonus;
  }

  private calculateBaseRuby(bossKills: number, currentLevel: number): number {
    const bossRuby = bossKills * 2;
    const levelBonus = currentLevel >= 10 ? Math.floor(currentLevel / 5) : 0;
    
    return bossRuby + levelBonus;
  }

  private getXPRequiredForLevel(level: number): number {
    return 100 + (level - 1) * 50;
  }

  private checkUnlockedContent(level: number): string[] {
    return this.unlockNewContent(level);
  }

  private showLevelUpEffect(): void {
    // Create level up notification
    const levelUpText = this.scene.add.text(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY - 100,
      `LEVEL UP!\nLevel ${this.gameLevel}`,
      {
        fontSize: '32px',
        color: '#FFD700',
        fontFamily: 'Arial',
        fontStyle: 'bold',
        align: 'center',
        shadow: { offsetX: 2, offsetY: 2, color: '#000000', blur: 4, fill: true }
      }
    ).setOrigin(0.5);
    
    // Animate level up text
    this.scene.tweens.add({
      targets: levelUpText,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 300,
      ease: 'Power2',
      yoyo: true,
      repeat: 1,
      onComplete: () => {
        this.scene.tweens.add({
          targets: levelUpText,
          alpha: 0,
          y: levelUpText.y - 50,
          duration: 1500,
          ease: 'Power2',
          onComplete: () => levelUpText.destroy()
        });
      }
    });
    
    // Create particle effects
    for (let i = 0; i < 20; i++) {
      const particle = this.scene.add.circle(
        this.scene.cameras.main.centerX + (Math.random() - 0.5) * 200,
        this.scene.cameras.main.centerY + (Math.random() - 0.5) * 200,
        Math.random() * 5 + 2,
        0xFFD700
      );
      
      this.scene.tweens.add({
        targets: particle,
        y: particle.y - 100,
        alpha: 0,
        scaleX: 0.1,
        scaleY: 0.1,
        duration: 2000,
        ease: 'Power2',
        delay: i * 50,
        onComplete: () => particle.destroy()
      });
    }
  }

  private showUnlockedContent(unlockedContent: string[]): void {
    if (unlockedContent.length === 0) return;
    
    unlockedContent.forEach((content, index) => {
      const unlockText = this.scene.add.text(
        this.scene.cameras.main.centerX,
        this.scene.cameras.main.centerY + 50 + index * 30,
        `${t('unlocked')}: ${content}`,
        {
          fontSize: '18px',
          color: '#00FF00',
          fontFamily: 'Arial',
          backgroundColor: 'rgba(0,0,0,0.7)',
          padding: { x: 10, y: 5 }
        }
      ).setOrigin(0.5);
      
      this.scene.tweens.add({
        targets: unlockText,
        alpha: 0,
        y: unlockText.y - 30,
        duration: 3000,
        ease: 'Power2',
        delay: index * 500,
        onComplete: () => unlockText.destroy()
      });
    });
    
    // Play unlock sound
    this.audioManager.playSFX(SoundEffect.ITEM_UNLOCK);
  }

  // Khi kết thúc ván, cộng XP vào profile
  static addXPToProfile(profile: any, xp: number): void {
    if (!profile.profileLevel) profile.profileLevel = 1;
    if (!profile.profileXP) profile.profileXP = 0;
    profile.profileXP += xp;
    // Tăng profileLevel nếu đủ XP
    let xpToNext = 100 + (profile.profileLevel - 1) * 50;
    while (profile.profileXP >= xpToNext) {
      profile.profileLevel++;
      profile.profileXP -= xpToNext;
      xpToNext = 100 + (profile.profileLevel - 1) * 50;
    }
  }

  // Progress tracking methods
  updateSessionTime(delta: number): void {
    this.sessionTime += delta;
  }

  getProgressStats(): {
    gameXP: number;
    gameLevel: number;
    xpToNextLevel: number;
    totalMonsterKills: number;
    totalBossKills: number;
    sessionTime: number;
  } {
    return {
      gameXP: this.gameXP,
      gameLevel: this.gameLevel,
      xpToNextLevel: this.getXPRequiredForLevel(this.gameLevel + 1),
      totalMonsterKills: this.totalMonsterKills,
      totalBossKills: this.totalBossKills,
      sessionTime: this.sessionTime
    };
  }

  // Getters
  getGameLevel(): number { return this.gameLevel; }
  getGameXP(): number { return this.gameXP; }
  getXPToNextLevel(): number { return this.getXPRequiredForLevel(this.gameLevel + 1); }
  getMonsterKills(): number { return this.totalMonsterKills; }
  getBossKills(): number { return this.totalBossKills; }
  getSessionTime(): number { return this.sessionTime; }

  // Setters for initialization
  setLevel(level: number): void { this.gameLevel = level; }
  setXP(xp: number): void { this.gameXP = xp; }
  setMonsterKills(kills: number): void { this.totalMonsterKills = kills; }
  setBossKills(kills: number): void { this.totalBossKills = kills; }
} 